/**
 * Schema marker for the `carrier_mcp_audit` Analytics Engine dataset.
 *
 * THE PROBLEM
 * -----------
 * Two writers share this dataset and their blob positions disagree:
 *
 *   writeAudit (audit.ts)              blob5 = sub,          2 doubles
 *   writeCarrierAskAudit               blob5 = intent_hash,  6 doubles
 *
 * Both set blob1 = "carrier_ask" for a carrier_ask call, so a query filtering on
 * blob1 gets rows from both, and reading blob5 as a user id silently mixes a
 * SHA-256 hash into per-tenant results. Confirmed live: one probe produced four
 * rows, two per call.
 *
 * Until now the only way to tell them apart was to infer from shape — the
 * generic row has two doubles, the carrier_ask row has six. That is not a
 * contract, it is a coincidence that any future field breaks.
 *
 * THE FIX, AND WHY IT LOOKS LIKE THIS
 * -----------------------------------
 * A marker at a FIXED index in every writer. Same index everywhere is the whole
 * point: a marker at position 9 in one writer and 10 in another leaves a query
 * no better off than before, since it still has to know which writer it is
 * reading to know where to look.
 *
 * Blob positions are the query contract, so the marker is APPENDED at a slot
 * beyond every current field and the gap is padded. Nothing existing moves.
 * `MARKER_INDEX` is deliberately well clear of both writers' current widths so
 * either can gain fields without a migration.
 *
 * IT MUST NEVER THROW
 * -------------------
 * `writeAudit` calls `writeDataPoint` with no try/catch, so anything thrown here
 * propagates into the tool call. A writer that outgrows MARKER_INDEX therefore
 * degrades — the marker lands late rather than raising — and
 * `audit-schema.test.ts` fails in CI instead. Enforcement belongs in the test,
 * not in a code path that runs beside a customer's request.
 */

/**
 * Blob index of the schema marker. AE allows 20 blobs; both writers currently
 * use fewer than 10, so 11 leaves either room to grow.
 */
export const MARKER_INDEX = 11;

/** One value per writer. Versioned, so a future layout change is nameable. */
export const SCHEMA_GENERIC = "v1-generic";
export const SCHEMA_CARRIER_ASK = "v1-carrier-ask";
/**
 * The intelligence composites' model calls.
 *
 * A third writer rather than a reuse of SCHEMA_CARRIER_ASK, because the two
 * answer different questions and merging them would make each one's counts
 * unreadable. But the positions the DETECTION query needs — sub, model_id, tier
 * at blobs 6/7/8, and the token doubles at 1..4 — are deliberately identical to
 * carrier_ask's, so "is the AI actually running?" is one statement across both
 * AI surfaces instead of two that have to be reconciled by hand.
 *
 * No writer in THIS package emits it today: stdio has no intelligence audit
 * writer. It is declared anyway, because `ALL_SCHEMA_MARKERS` is the set of
 * markers the shared `carrier_mcp_audit` dataset can legitimately carry, not
 * the set this one surface happens to write. Omitting it made the file that
 * exists to prevent parity gaps carry one — and a validator built on the short
 * list would reject the Worker's real rows as unknown.
 */
export const SCHEMA_INTELLIGENCE = "v1-intelligence";

/** Every marker this dataset can legitimately carry. */
export const ALL_SCHEMA_MARKERS = [
  SCHEMA_GENERIC,
  SCHEMA_CARRIER_ASK,
  SCHEMA_INTELLIGENCE,
] as const;

/**
 * Pad `blobs` so `marker` lands at MARKER_INDEX, then append it.
 *
 * Returns a new array; does not mutate. Never throws: a writer already wider
 * than MARKER_INDEX gets its marker appended at the end instead, which keeps the
 * write working while the test flags the drift.
 */
export function withSchemaMarker(blobs: string[], marker: string): string[] {
  const out = [...blobs];
  while (out.length < MARKER_INDEX) out.push("");
  out.push(marker);
  return out;
}
